import { FC, ReactNode, useRef } from "react";
import { FormInstance, Input, ModalProps, Select, Switch } from "antd";
import { useTranslation } from "react-i18next";

import SModalForm from "@/styles/SModalForm";
import { CountrySelect } from "@/components/Selector";
import {
  ShipAddress,
  ShipAddressDefault,
  ShipAddressParams,
  ShipAddressTypeOption,
} from "@/types/shipAddress";

type ShipAddressFormValue = Omit<ShipAddressParams, "default"> & {
  default: boolean;
};

interface ShipAddressFormProps extends ModalProps {
  data: ShipAddress | null;
  typeOptions: Array<ShipAddressTypeOption>;
  handleCancel: () => void;
  handleOk: (data: ShipAddressParams) => Promise<void>;
  leftTitle: ReactNode;
}

const ShipAddressForm: FC<ShipAddressFormProps> = ({
  data,
  typeOptions,
  handleCancel,
  handleOk,
  leftTitle,
  ...modalProps
}) => {
  const { t } = useTranslation();
  const formRef = useRef<FormInstance<ShipAddressFormValue>>(null);
  const formData: ShipAddressFormValue = data
    ? {
        name: data.name,
        address: data.address,
        phone: data.phone,
        country: data.country,
        type: data.type,
        default: data.default === ShipAddressDefault.Yes,
        remark: data.remark,
      }
    : {
        name: "",
        address: "",
        type: 1,
        default: false,
      };

  return (
    <SModalForm<ShipAddressFormValue>
      {...modalProps}
      width={600}
      modelFormRef={formRef}
      data={formData}
      leftTitle={leftTitle}
      handleCancel={handleCancel}
      handleOk={values => handleOk({
        ...values,
        default: values.default
          ? ShipAddressDefault.Yes
          : ShipAddressDefault.No,
      })}
      formItems={[
        {
          label: t("sale.shipAddressName"),
          name: "name",
          required: true,
          rules: [{ required: true, message: t("sale.plShipAddressName") }],
          children: (
            <Input
              placeholder={t("sale.plShipAddressName")}
              maxLength={100}
              showCount
            />
          ),
        },
        {
          label: t("sale.address"),
          name: "address",
          required: true,
          rules: [{ required: true, message: t("sale.plAddress") }],
          children: (
            <Input.TextArea
              placeholder={t("sale.plAddress")}
              autoSize={{ minRows: 2, maxRows: 4 }}
              maxLength={500}
              showCount
            />
          ),
        },
        {
          label: t("sale.phone"),
          name: "phone",
          children: <Input placeholder={t("sale.plPhone")} maxLength={50} showCount />,
        },
        {
          label: t("sale.country"),
          name: "country",
          children: <CountrySelect />,
        },
        {
          label: t("sale.shipAddressType"),
          name: "type",
          required: true,
          rules: [{ required: true, message: t("sale.shipAddressType") }],
          children: (
            <Select
              placeholder={t("sale.shipAddressType")}
              options={typeOptions.map(item => ({
                value: item.key,
                label: item.desc,
              }))}
            />
          ),
        },
        {
          label: t("sale.defaultAddress"),
          name: "default",
          valuePropName: "checked",
          children: <Switch />,
        },
        {
          label: t("sale.remark"),
          name: "remark",
          children: (
            <Input.TextArea
              placeholder={t("sale.remark")}
              autoSize={{ minRows: 2, maxRows: 4 }}
              maxLength={500}
              showCount
            />
          ),
        },
      ]}
    />
  );
};

export default ShipAddressForm;
